import React from 'react';
import { C } from '../../theme.js';
import { Alert, Eyebrow, FieldLabel, TextInput } from '../../components/Primitives.jsx';
import { MultiColsDropdown } from './MultiColsDropdown.jsx';
import { resolveLiveRows } from './columnClassification.js';
import { hasUpstreamSplit, SOURCE_ROWS } from '../../data.js';
import { OutputColumnNamesField } from '../shared/OutputColumnNamesField.jsx';

function getFitRows(rows) {
  if (!rows || !rows.length) return [];
  for (const key of Object.keys(rows[0] || {})) {
    const vals = new Set(rows.map(r => r[key]).filter(v => v !== null && v !== ''));
    if (vals.size > 0 && Array.from(vals).every(v => v === 'train' || v === 'test')) {
      const filtered = rows.filter(r => r[key] === 'train');
      if (filtered.length > 0) return filtered;
      break;
    }
  }
  return rows;
}

// Unique categories for ONE column, from training rows only.
function detectCategoriesForColumn(rows, col) {
  if (!rows || !rows.length || !col) return [];
  const fitRows = getFitRows(rows);
  const set = new Set();
  for (const r of fitRows) {
    const v = r[col];
    if (v !== null && v !== undefined && v !== '') set.add(String(v));
  }
  return Array.from(set).sort();
}

// Unique categories across every selected column — used only in the
// single-column-selected UI, where "across every selected column" and
// "for the one selected column" are the same set.
function detectCategories(rows, cols) {
  if (!rows || !rows.length || !cols || !cols.length) return [];
  const fitRows = getFitRows(rows);
  const set = new Set();
  for (const r of fitRows) {
    for (const col of cols) {
      const v = r[col];
      if (v !== null && v !== undefined && v !== '') set.add(String(v));
    }
  }
  return Array.from(set).sort();
}

function CategoryTable({ title, categories, valueFor, onChangeValue }) {
  return (
    <div style={{
      background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 8, overflow: 'hidden', marginBottom: title ? 10 : 0
    }}>
      <div style={{
        padding: '6px 10px', background: C.input, borderBottom: `1px solid ${C.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <span style={{ fontSize: '.72rem', fontWeight: 700, color: C.fg1 }}>
          {title || `${categories.length} unique ${categories.length === 1 ? 'category' : 'categories'} detected`}
        </span>
        {title && (
          <span style={{ fontSize: '.7rem', color: C.fg4 }}>
            {categories.length} {categories.length === 1 ? 'category' : 'categories'}
          </span>
        )}
      </div>
      <div style={{ maxHeight: 240, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.78rem', textAlign: 'left' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.borderSubtle}`, color: C.fg4 }}>
              <th style={{ padding: '5px 10px', fontWeight: 600 }}>Category</th>
              <th style={{ padding: '5px 10px', fontWeight: 600 }}>Map to</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((cat) => (
              <tr key={cat} style={{ borderBottom: `1px solid ${C.borderSubtle}` }}>
                <td style={{ padding: '4px 10px', color: C.fg1 }}>{cat}</td>
                <td style={{ padding: '4px 10px' }}>
                  <TextInput
                    value={valueFor(cat)}
                    onChange={(v) => onChangeValue(cat, v)}
                    placeholder="Auto"
                    style={{ width: 90 }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * OrdinalEncodingInspector
 * Encodes categorical features as ordinal integers.
 *
 * With exactly one column selected, ranks live in the legacy `mapping`
 * dict (unchanged from before per-column support existed). With 2+
 * columns, each column gets its own detected-categories table backed by
 * `column_mappings[col]` — so e.g. a "Priority" column's "Low" and a
 * "Risk" column's "Low" can be ranked independently instead of sharing one
 * value. A column with no per-column override yet still shows/uses
 * whatever the legacy `mapping` currently provides for a category name it
 * shares (matching the backend's fallback — see column_mapping_for() in
 * server/app/engine/helpers.py) until the user actually edits that column's
 * own row, at which point only that column's override applies going
 * forward.
 */
export function OrdinalEncodingInspector({ step, onChange, pipeline, uploadedRows }) {
  const c = step.config || {};
  const sel = Array.isArray(c.columns) ? c.columns : (c.column ? [c.column] : []);
  const u = (patch) => onChange({ ...step, config: { ...c, ...patch } });
  const splitReady = pipeline ? hasUpstreamSplit(pipeline.steps, step.id) : true;

  const rows = resolveLiveRows(uploadedRows, pipeline, SOURCE_ROWS);
  const mapping = c.mapping || {};
  const columnMappings = c.column_mappings || {};
  const multiColumn = sel.length >= 2;

  const setMappingValue = (cat, raw) => {
    const nextMapping = { ...mapping };
    if (raw === '') {
      delete nextMapping[cat];
    } else {
      const num = Number(raw);
      nextMapping[cat] = Number.isFinite(num) ? num : raw;
    }
    u({ mapping: nextMapping });
  };

  const setColumnMappingValue = (col, cat, raw) => {
    const colMap = { ...(columnMappings[col] || {}) };
    if (raw === '') {
      delete colMap[cat];
    } else {
      const num = Number(raw);
      colMap[cat] = Number.isFinite(num) ? num : raw;
    }
    u({ column_mappings: { ...columnMappings, [col]: colMap } });
  };

  // Single-column display (unchanged from before per-column support).
  const detected = detectCategories(rows, sel);
  const staleKeys = Object.keys(mapping).filter((k) => !detected.includes(k));
  const displayCategories = [...detected, ...staleKeys.sort()];

  return (
    <div>
      <Eyebrow>Ordinal Encoding</Eyebrow>
      <div style={{ color: C.fg4, fontSize: '.78rem', margin: '4px 0 10px', lineHeight: 1.5 }}>
        Encode categorical features into integer values.
        Pick a rank for each detected category, or leave every field blank to auto-assign integers alphabetically.
        {multiColumn && ' With multiple columns selected, each has its own categories and ranks below.'}
      </div>
      {!splitReady && (
        <Alert kind="warn" style={{ marginBottom: 10 }}>
          No Train / Test Split step found upstream — this will fit using all rows. If the result feeds a model, add a Train / Test Split step first to avoid data leakage.
        </Alert>
      )}
      <MultiColsDropdown selected={sel} onChange={(v) => u({ columns: v, column: v[0] || null })} categorical />
      <OutputColumnNamesField
        columns={sel}
        value={c.output_columns}
        onChange={(v) => u({ output_columns: v })}
        legacyValue={c.output_column}
        autoName={(col) => `${col}_ordinal_encoded`}
      />
      <div style={{ marginTop: 10 }}>
        <FieldLabel hint={multiColumn
          ? "Detected from each selected column's own training data — type the integer each column's category should map to. Leave a category blank and it's mapped to None for that column (unless every field for that column is blank, which auto-assigns ranks alphabetically instead)."
          : "Detected from your selected column's training data — type the integer each category should map to. Leave a category blank and it's mapped to None (unless every field is blank, which auto-assigns ranks alphabetically instead)."}>
          Category Mapping
        </FieldLabel>
        {sel.length === 0 ? (
          <div style={{
            padding: 10, borderRadius: 8, background: C.surface,
            border: `1px dashed ${C.border}`, color: C.fg4, fontSize: '.76rem', textAlign: 'center'
          }}>
            Select a column above to see its unique categories here.
          </div>
        ) : multiColumn ? (
          sel.every((col) => detectCategoriesForColumn(rows, col).length === 0 && !Object.keys(columnMappings[col] || {}).length)
            ? (
              <div style={{
                padding: 10, borderRadius: 8, background: C.surface,
                border: `1px dashed ${C.border}`, color: C.fg4, fontSize: '.76rem', textAlign: 'center'
              }}>
                No categories detected yet — data may not be loaded for this pipeline.
              </div>
            )
            : sel.map((col) => {
              const colDetected = detectCategoriesForColumn(rows, col);
              const colMap = columnMappings[col] || {};
              const colStale = Object.keys(colMap).filter((k) => !colDetected.includes(k));
              const colCategories = [...colDetected, ...colStale.sort()];
              if (colCategories.length === 0) return null;
              return (
                <CategoryTable
                  key={col}
                  title={`Column: ${col}`}
                  categories={colCategories}
                  valueFor={(cat) => (cat in colMap ? colMap[cat] : (mapping[cat] ?? ''))}
                  onChangeValue={(cat, v) => setColumnMappingValue(col, cat, v)}
                />
              );
            })
        ) : displayCategories.length === 0 ? (
          <div style={{
            padding: 10, borderRadius: 8, background: C.surface,
            border: `1px dashed ${C.border}`, color: C.fg4, fontSize: '.76rem', textAlign: 'center'
          }}>
            No categories detected yet — data may not be loaded for this pipeline.
          </div>
        ) : (
          <CategoryTable
            categories={displayCategories}
            valueFor={(cat) => mapping[cat] ?? ''}
            onChangeValue={setMappingValue}
          />
        )}
      </div>
    </div>
  );
}
